// Status mapping utility for customer-facing order statuses
// This consolidates internal backend statuses into customer-friendly labels

export const getDisplayStatus = (status = "") => {
  const normalizedStatus = status.toLowerCase().replace(/\s+/g, "_");

  switch (normalizedStatus) {
    case "placed":
      return "Placed";

    // Consolidate all internal production statuses into "Awaiting Process"
    case "processing":
    case "printing_started":
    case "printing started":
    case "production_completed":
    case "production completed":
    case "quality_check":
    case "quality check":
    case "packaging":
      return "Awaiting Process";

    case "shipped":
      return "Shipped";

    case "delivered":
      return "Delivered";

    case "completed":
      return "Completed";

    case "return_requested":
    case "return requested":
    case "returned":
      return "Return Requested";

    case "return_approved":
    case "return approved":
      return "Return Approved";

    case "return_rejected":
    case "return rejected":
      return "Return Rejected";

    case "cancel_requested":
    case "cancel requested":
    case "cancellation_requested":
    case "cancellation requested":
      return "Cancel Requested";

    case "cancelled":
    case "cancelled_(not_refunded)":
    case "cancelled (not refunded)":
      return "Cancelled";

    case "refund_processing":
    case "refund processing":
      return "Refund Processing";

    case "refunded":
    case "refund":
      return "Refunded";

    case "rejected":
      return "Rejected";

    case "inspection_pending":
    case "inspection pending":
      return "Inspection Pending";

    case "partial_refund":
    case "partial refund":
      return "Partial Refund";

    case "replacement_processing":
    case "replacement processing":
      return "Replacement Processing";

    case "returned_(no_refund)":
    case "returned (no refund)":
    case "returned_no_refund":
      return "Returned (No Refund)";

    default:
      return status;
  }
};

// Get customer-friendly progress configuration
export const getProgressConfig = (status) => {
  const displayStatus = getDisplayStatus(status);

  switch (displayStatus) {
    case "Placed":
      return {
        width: "w-1/4",
        color: "bg-teal-500",
        label: "Order Placed",
        percentage: 25
      };

    case "Awaiting Process":
      return {
        width: "w-2/4",
        color: "bg-blue-500",
        label: "Awaiting Process",
        percentage: 50
      };

    case "Shipped":
      return {
        width: "w-3/4",
        color: "bg-violet-500",
        label: "Shipped",
        percentage: 75
      };

    case "Delivered":
    case "Completed":
      return {
        width: "w-full",
        color: "bg-emerald-500",
        label: "Delivered",
        percentage: 100
      };

    // Special states - show as completed but with different colors
    case "Return Requested":
      return {
        width: "w-full",
        color: "bg-sky-500",
        label: "Return Requested",
        percentage: 100
      };

    case "Return Approved":
      return {
        width: "w-full",
        color: "bg-teal-400",
        label: "Return Approved",
        percentage: 100
      };

    case "Return Rejected":
      return {
        width: "w-full",
        color: "bg-red-500",
        label: "Return Rejected",
        percentage: 100
      };

    case "Cancel Requested":
      return {
        width: "w-2/4",
        color: "bg-rose-400",
        label: "Cancel Requested",
        percentage: 50
      };

    case "Cancelled":
      return {
        width: "w-1/4",
        color: "bg-red-500",
        label: "Cancelled",
        percentage: 25
      };

    case "Refund Processing":
      return {
        width: "w-full",
        color: "bg-indigo-400",
        label: "Refund Processing",
        percentage: 100
      };

    case "Refunded":
      return {
        width: "w-full",
        color: "bg-green-500",
        label: "Refunded",
        percentage: 100
      };

    case "Rejected":
      return {
        width: "w-1/4",
        color: "bg-rose-600",
        label: "Rejected",
        percentage: 25
      };

    case "Inspection Pending":
      return {
        width: "w-full",
        color: "bg-purple-400",
        label: "Inspection Pending",
        percentage: 100
      };

    case "Partial Refund":
      return {
        width: "w-full",
        color: "bg-lime-500",
        label: "Partial Refund",
        percentage: 100
      };

    case "Replacement Processing":
      return {
        width: "w-full",
        color: "bg-sky-400",
        label: "Replacement Processing",
        percentage: 100
      };

    case "Returned (No Refund)":
      return {
        width: "w-full",
        color: "bg-red-700",
        label: "Returned (No Refund)",
        percentage: 100
      };

    default:
      return {
        width: "w-1/4",
        color: "bg-gray-500",
        label: "Unknown",
        percentage: 25
      };
  }
};

// Get customer-facing status options for filters
export const getCustomerStatusOptions = () => [
  { value: "all", label: "All Orders" },
  { value: "Placed", label: "Order Placed" },
  { value: "Awaiting Process", label: "Awaiting Process" },
  { value: "Shipped", label: "Shipped" },
  { value: "Delivered", label: "Delivered" },
  { value: "Completed", label: "Completed" },
  { value: "Return Requested", label: "Return Requested" },
  { value: "Return Approved", label: "Return Approved" },
  { value: "Return Rejected", label: "Return Rejected" },
  { value: "Cancel Requested", label: "Cancel Requested" },
  { value: "Cancelled", label: "Cancelled" },
  { value: "Refund Processing", label: "Refund Processing" },
  { value: "Refunded", label: "Refunded" },
  { value: "Rejected", label: "Rejected" }
];

// Check if status allows reordering
export const canReorder = (status) => {
  const displayStatus = getDisplayStatus(status);
  return [
    "Delivered",
    "Completed",
    "Cancelled",
    "Refunded",
    "Rejected",
    "Returned (No Refund)"
  ].includes(displayStatus);
};

// Check if status allows returns
export const canReturn = (status, items = []) => {
  const displayStatus = getDisplayStatus(status);
  return (displayStatus === "Delivered" || displayStatus === "Completed") &&
         items.some(item => item.customisations === "No" || item.returnStatus === "returnable");
};

// Check if status allows reviews
export const canReview = (status) => {
  const displayStatus = getDisplayStatus(status);
  return [
    "Delivered",
    "Completed",
    "Return Requested",
    "Return Approved",
    "Returned",
    "Refunded",
    "Partial Refund"
  ].includes(displayStatus);
};

// Check if status allows cancellation
export const canCancel = (status) => {
  const displayStatus = getDisplayStatus(status);
  return ["Placed", "Awaiting Process"].includes(displayStatus);
};

// Get delivery status message
export const getDeliveryMessage = (status, estimatedDelivery, actualDelivery) => {
  const displayStatus = getDisplayStatus(status);

  switch (displayStatus) {
    case "Delivered":
      return actualDelivery
        ? `Delivered on ${new Date(actualDelivery).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}`
        : "Order delivered";

    case "Completed":
      return "Order completed";

    case "Cancelled":
      return "Order cancelled";

    case "Refunded":
      return "Order refunded";

    case "Rejected":
      return "Order rejected";

    case "Return Requested":
      return "Return requested";

    case "Return Approved":
      return "Return approved";

    case "Return Rejected":
      return "Return rejected";

    case "Cancel Requested":
      return "Cancellation requested";

    case "Returned":
      return "Product received";

    case "Returned (No Refund)":
      return "Returned - No refund";

    case "Inspection Pending":
      return "Under inspection";

    case "Partial Refund":
      return "Partial refund processed";

    case "Replacement Processing":
      return "Replacement in progress";

    case "Refund Processing":
      return "Refund processing";

    case "Awaiting Process":
      return "Order is being processed";

    case "Placed":
      return "Order placed successfully";

    case "Shipped":
      return estimatedDelivery
        ? `Expected delivery: ${new Date(estimatedDelivery).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}`
        : "Order shipped";

    default:
      return estimatedDelivery
        ? `Expected delivery: ${new Date(estimatedDelivery).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}`
        : "Processing order";
  }
};
